import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useDictText } from '../../../components/DictSelect';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import { BundleSkuItem, MaterialBundlePayload } from '../../../api/bundle';
import { listSkus, SkuRow } from '../../../api/material';
import { CapType } from '../../../api/captype';
import { recommendTips, scopeText, Tip, listAllTips } from '../../../api/tips';
import { ApiError } from '../../../api/request';
import { TipForm, TipTypeTag } from '../TipWidgets';
import { useCan } from '../../../store';
import { IssueByPath } from './useBundleDraft';
import { SkuPropSelect } from '../../../components/SkuProp';
import { SkuSpecInput } from '../../../components/SkuSpec';
import { EMPTY_SKU_SPEC, useSkuSchemas, validateSkuSpec } from '../../../utils/skuSpec';

// 向导第 ③ 步: 关联数据 (SKU / 工艺类型 / 小贴士), 三个分区都可以为空.
//
// 两类子资源在这里分野 (设计 07 §3):
//   随材料落库的 —— SKU 新行、能力位、贴士挂载: 只进草稿, 审批通过后与材料同一事务创建;
//   全局字典类 —— 贴士条目: 在这里就地创建即刻进库 (走贴士自己的审批), 套件只引用它的 code.
// 工艺类型两者都不是: 它是字典, 向导内**只能勾选已有项**, 缺了去「工艺类型管理」维护.
//
// 这里不展示"该工艺类型下有几台设备": 台数只是参考信息, 展开看机器清单要读设备
// (POST /v1/cap-types/devices = device:get), 而建材料的角色 (耗材业务) 没有设备读权 ——
// 为一个参考信息去要设备域的读权不划算, 整列去掉, 向导因此完全不依赖设备接口.

type Panel = 'sku' | 'cap' | 'tip';

interface Props {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    capTypes: CapType[];
    issueByPath: IssueByPath;
}

function StepRelations({ payload, setPayload, capTypes, issueByPath }: Props) {
    const [panel, setPanel] = useState<Panel>('sku');
    const counts = {
        sku: payload.skus?.length || 0,
        cap: payload.capabilities?.length || 0,
        tip: payload.tips?.tipCodes?.length || 0,
    };
    const tabs: { key: Panel; label: string; n: number }[] = [
        { key: 'sku', label: 'SKU', n: counts.sku },
        { key: 'cap', label: '工艺类型', n: counts.cap },
        { key: 'tip', label: '小贴士', n: counts.tip },
    ];
    return (
        <>
            <div className="inline-flex gap-1 p-1 rounded-lg bg-slate-100">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => setPanel(t.key)}
                        className={`px-4 py-1 rounded-md text-xs ${
                            panel === t.key
                                ? 'bg-white text-brand-700 font-medium shadow-sm'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        {t.label}
                        {t.n > 0 && <span className="opacity-60"> · {t.n}</span>}
                    </button>
                ))}
            </div>
            {panel === 'sku' && (
                <SkuPanel
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={issueByPath}
                />
            )}
            {panel === 'cap' && (
                <CapPanel
                    payload={payload}
                    setPayload={setPayload}
                    capTypes={capTypes}
                    issueByPath={issueByPath}
                />
            )}
            {panel === 'tip' && (
                <TipPanel
                    payload={payload}
                    setPayload={setPayload}
                    issueByPath={issueByPath}
                />
            )}
        </>
    );
}

// ───────────────────────── SKU 分区 ─────────────────────────
// 两种来源: 从 SKU 库搜到的已有条目 (linked, 只追加一条绑定到本材料, 不改它的本体与主绑定),
// 与手填的新行 (随套件事务创建 + 首绑即主绑定). 两种在同一张表里, 用徽标区分.

function SkuPanel({
    payload,
    setPayload,
    issueByPath,
}: {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    issueByPath: IssueByPath;
}) {
    const skus = payload.skus || [];
    const [kw, setKw] = useState('');
    const [found, setFound] = useState<SkuRow[]>([]);
    const [searching, setSearching] = useState(false);
    const [err, setErr] = useState('');
    const [adding, setAdding] = useState(false);

    const setSkus = (next: BundleSkuItem[]) => setPayload({ ...payload, skus: next });

    const search = async () => {
        setSearching(true);
        setErr('');
        try {
            const res = await listSkus({ keyword: kw || undefined, page: 1, pageSize: 20 });
            setFound(res.list || []);
        } catch (e) {
            setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
        } finally {
            setSearching(false);
        }
    };

    const link = (r: SkuRow) => {
        if (skus.some((s) => s.skuCode === r.skuCode)) return;
        setSkus([...skus, { skuCode: r.skuCode, linked: true }]);
    };

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">关联已有 SKU</div>
                <div className="flex gap-2">
                    <input
                        className="inp flex-1"
                        value={kw}
                        onChange={(e) => setKw(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && search()}
                        placeholder="按 SKU 编码 / 商品名搜索"
                    />
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={search}
                        disabled={searching}
                    >
                        {searching ? '搜索中…' : '搜索'}
                    </button>
                </div>
                {err && <div className="text-xs text-rose-600">{err}</div>}
                {found.length > 0 && (
                    <div className="max-h-56 overflow-auto border border-slate-100 rounded">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="th">SKU 编码</th>
                                    <th className="th">已绑材料</th>
                                    <th className="th w-16" />
                                </tr>
                            </thead>
                            <tbody>
                                {found.map((r) => {
                                    const picked = skus.some((s) => s.skuCode === r.skuCode);
                                    return (
                                        <tr key={r.skuCode}>
                                            <td className="td font-mono text-xs">{r.skuCode}</td>
                                            <td className="td text-xs text-slate-500">
                                                {r.materials?.length
                                                    ? r.materials
                                                          .map(
                                                              (m) =>
                                                                  m.xcode +
                                                                  (m.isPrimary ? ' (主)' : ''),
                                                          )
                                                          .join(', ')
                                                    : '未绑定'}
                                            </td>
                                            <td className="td">
                                                <button
                                                    type="button"
                                                    className="text-xs text-brand-600 hover:underline disabled:text-slate-300 disabled:no-underline"
                                                    disabled={picked}
                                                    onClick={() => link(r)}
                                                >
                                                    {picked ? '已加入' : '＋ 关联'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="text-[11px] text-slate-400">
                    关联只会给该 SKU 追加一条到本材料的绑定, 不改它的本体字段, 也不动它原来的主绑定。
                </div>
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                    本次创建的 SKU
                    <button
                        type="button"
                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                        onClick={() => setAdding(true)}
                    >
                        ＋ 新建 SKU 行
                    </button>
                </div>
                {skus.length === 0 ? (
                    <div className="text-xs text-slate-400">
                        还没有 SKU。SKU 可以留到创建后在材料详情页再加。
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="th">SKU 编码</th>
                                <th className="th">商品名</th>
                                <th className="th">来源</th>
                                <th className="th w-14" />
                            </tr>
                        </thead>
                        <tbody>
                            {skus.map((s, i) => (
                                <tr key={s.skuCode}>
                                    <td className="td font-mono text-xs">
                                        {s.skuCode}
                                        <FieldIssue issue={issueByPath[`skus[${i}].skuCode`]} />
                                    </td>
                                    <td className="td text-xs">
                                        {s.linked ? (
                                            <span className="text-slate-400">以库中为准</span>
                                        ) : (
                                            s.productNameZh || s.productNameEn || '—'
                                        )}
                                    </td>
                                    <td className="td">
                                        <span
                                            className={`tag ${
                                                s.linked
                                                    ? 'bg-slate-100 text-slate-600'
                                                    : 'cap-on'
                                            }`}
                                        >
                                            {s.linked ? '关联已有' : '新建'}
                                        </span>
                                    </td>
                                    <td className="td">
                                        <button
                                            type="button"
                                            className="btn-danger-text"
                                            onClick={() =>
                                                setSkus(skus.filter((x) => x.skuCode !== s.skuCode))
                                            }
                                        >
                                            移除
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <NewSkuModal
                open={adding}
                exists={skus.map((s) => s.skuCode)}
                onClose={() => setAdding(false)}
                onAdd={(item) => {
                    setSkus([...skus, item]);
                    setAdding(false);
                }}
            />
        </>
    );
}

/** NewSkuModal 新建 SKU 行 (只进草稿, 提交整包时随材料一起创建). */
function NewSkuModal({
    open,
    exists,
    onClose,
    onAdd,
}: {
    open: boolean;
    exists: string[];
    onClose: () => void;
    onAdd: (s: BundleSkuItem) => void;
}) {
    const [form, setForm] = useState<BundleSkuItem>({ skuCode: '' });
    const [spec, setSpec] = useState(EMPTY_SKU_SPEC);
    const { byKey: specSchemas } = useSkuSchemas();
    const [err, setErr] = useState('');
    useEffect(() => {
        if (open) {
            setForm({ skuCode: '' });
            setSpec(EMPTY_SKU_SPEC);
            setErr('');
        }
    }, [open]);
    const submit = () => {
        const code = form.skuCode.trim();
        if (!code) {
            setErr('SKU 编码必填');
            return;
        }
        if (exists.includes(code)) {
            setErr('该 SKU 编码已在列表里');
            return;
        }
        const [specPayload, specErr] = validateSkuSpec(form.prop || '', spec, specSchemas);
        if (specErr) {
            setErr(specErr);
            return;
        }
        // 没选规格类型不进套件 (提交时空值键被剔除, 与其它可选字段一致)
        onAdd({ ...form, ...(specPayload.specGroup ? specPayload : {}), skuCode: code });
    };
    return (
        <Modal open={open} title="新建 SKU" width={520} onClose={onClose}>
            <div className="space-y-3">
                <div>
                    <label className="field-label">
                        <span className="text-rose-500">* </span>SKU 编码
                    </label>
                    <input
                        className="inp font-mono"
                        value={form.skuCode}
                        onChange={(e) => setForm({ ...form, skuCode: e.target.value })}
                        placeholder="SKU-XXXX-3MM"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">商品名 (中文)</label>
                        <input
                            className="inp"
                            value={form.productNameZh || ''}
                            onChange={(e) =>
                                setForm({ ...form, productNameZh: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <label className="field-label">商品名 (英文)</label>
                        <input
                            className="inp"
                            value={form.productNameEn || ''}
                            onChange={(e) =>
                                setForm({ ...form, productNameEn: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <label className="field-label">属性</label>
                        <SkuPropSelect
                            className="sel"
                            value={form.prop || ''}
                            onChange={(v) => setForm({ ...form, prop: v })}
                        />
                    </div>
                    <div>
                        <label className="field-label">CMS 耗材 ID</label>
                        <input
                            className="inp font-mono"
                            type="number"
                            value={form.cmsId ?? ''}
                            onChange={(e) => {
                                const n = Number(e.target.value);
                                setForm({
                                    ...form,
                                    cmsId: e.target.value === '' || isNaN(n) ? undefined : n,
                                });
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <SkuSpecInput prop={form.prop || ''} value={spec} onChange={setSpec} />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">备注</label>
                        <input
                            className="inp"
                            value={form.remark || ''}
                            onChange={(e) => setForm({ ...form, remark: e.target.value })}
                        />
                    </div>
                </div>
                {err && <div className="text-xs text-rose-600">{err}</div>}
                <div className="flex justify-end gap-2">
                    <button type="button" className="btn-ghost" onClick={onClose}>
                        取消
                    </button>
                    <button type="button" className="btn-primary" onClick={submit}>
                        加入套件
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ───────────────────────── 工艺类型分区 ─────────────────────────

function CapPanel({
    payload,
    setPayload,
    capTypes,
    issueByPath,
}: {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    capTypes: CapType[];
    issueByPath: IssueByPath;
}) {
    const picked = payload.capabilities || [];
    const canMaintain = useCan('cap_type', 'create');
    const lightText = useDictText('light_source');
    const actionText = useDictText('cap_action');

    const toggle = (capKey: string) => {
        const next = picked.includes(capKey)
            ? picked.filter((c) => c !== capKey)
            : [...picked, capKey];
        setPayload({ ...payload, capabilities: next });
    };

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">适配的工艺类型</div>
                {capTypes.length === 0 ? (
                    <div className="text-xs text-slate-400">
                        还没有任何工艺类型定义。
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        {capTypes.map((c) => {
                            const on = picked.includes(c.capKey);
                            return (
                                <div
                                    key={c.capKey}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50"
                                >
                                    <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={on}
                                            onChange={() => toggle(c.capKey)}
                                        />
                                        <span className={`tag ${on ? 'cap-on' : 'cap-off'}`}>
                                            {c.label}
                                        </span>
                                        <span className="text-[11px] text-slate-400 truncate">
                                            {lightText(c.lightSource)} · {actionText(c.action)}
                                        </span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                )}
                <FieldIssue issue={issueByPath['capabilities']} />
                <div className="text-[11px] text-slate-400">
                    工艺类型是字典, 向导里只能勾选已有项。
                    {canMaintain ? (
                        <>
                            {' '}
                            找不到想要的?{' '}
                            <Link
                                to="/craft-types"
                                target="_blank"
                                className="text-brand-600 hover:underline"
                            >
                                去「工艺类型管理」维护 ↗
                            </Link>
                            (新开页, 本页草稿会保留)
                        </>
                    ) : (
                        ' 找不到想要的请联系管理员在「工艺类型管理」中维护。'
                    )}
                </div>
            </div>
        </>
    );
}

// ───────────────────────── 小贴士分区 ─────────────────────────

function TipPanel({
    payload,
    setPayload,
    issueByPath,
}: {
    payload: MaterialBundlePayload;
    setPayload: (p: MaterialBundlePayload) => void;
    issueByPath: IssueByPath;
}) {
    const type1 = payload.material.type1;
    const picked = payload.tips?.tipCodes || [];
    const canCreateTip = useCan('tip', 'create');
    const [recs, setRecs] = useState<Tip[]>([]);
    const [all, setAll] = useState<Tip[]>([]);
    const [kw, setKw] = useState('');
    const [creating, setCreating] = useState(false);

    // 推荐条目: 同一级类别下现有材料在用的通用贴士, 默认全选 (口径同原新建弹窗)
    useEffect(() => {
        if (!type1) {
            setRecs([]);
            return;
        }
        let alive = true;
        recommendTips(type1)
            .then((list) => {
                if (!alive) return;
                setRecs(list || []);
                // 首次进本步时默认全选推荐项 (用户没动过 tips 才自动填)
                if (!payload.tips) {
                    setPayload({
                        ...payload,
                        tips: { tipCodes: (list || []).map((t) => t.tipCode) },
                    });
                }
            })
            .catch(() => alive && setRecs([]));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type1]);

    const loadAll = () => {
        if (all.length) return;
        listAllTips()
            .then(setAll)
            .catch(() => undefined);
    };

    const setCodes = (codes: string[]) =>
        setPayload({ ...payload, tips: { ...(payload.tips || {}), tipCodes: codes } });

    const toggle = (code: string) =>
        setCodes(picked.includes(code) ? picked.filter((c) => c !== code) : [...picked, code]);

    const byCode = useMemo(() => {
        const m: Record<string, Tip> = {};
        [...recs, ...all].forEach((t) => {
            m[t.tipCode] = t;
        });
        return m;
    }, [recs, all]);

    const searchResults = useMemo(() => {
        if (!kw.trim()) return [];
        const q = kw.trim().toLowerCase();
        return all
            .filter(
                (t) =>
                    t.title.toLowerCase().includes(q) ||
                    t.tipCode.toLowerCase().includes(q),
            )
            .slice(0, 20);
    }, [all, kw]);

    return (
        <>
            {recs.length > 0 && (
                <div className="card p-4 space-y-2">
                    <div className="text-sm font-semibold">
                        本类别推荐 ({recs.length})
                        <span className="text-xs font-normal text-slate-400">
                            {' '}
                            · 同类材料在用的通用条目, 默认全选
                        </span>
                    </div>
                    {recs.map((t) => (
                        <label
                            key={t.tipCode}
                            className="flex items-start gap-2 text-xs cursor-pointer py-0.5"
                        >
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={picked.includes(t.tipCode)}
                                onChange={() => toggle(t.tipCode)}
                            />
                            <TipTypeTag type={t.type} />
                            <span className="flex-1 min-w-0">
                                <span className="text-slate-700">{t.title}</span>
                                <span className="text-slate-400"> · {scopeText(t)}</span>
                            </span>
                        </label>
                    ))}
                </div>
            )}

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                    从小贴士库添加
                    <button
                        type="button"
                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                        onClick={() => setCreating(true)}
                        hidden={!canCreateTip}
                    >
                        ＋ 新建贴士条目
                    </button>
                </div>
                <input
                    className="inp"
                    value={kw}
                    onFocus={loadAll}
                    onChange={(e) => setKw(e.target.value)}
                    placeholder="按标题 / 编码搜索已有条目"
                />
                {searchResults.map((t) => (
                    <label
                        key={t.tipCode}
                        className="flex items-start gap-2 text-xs cursor-pointer py-0.5"
                    >
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={picked.includes(t.tipCode)}
                            onChange={() => toggle(t.tipCode)}
                        />
                        <TipTypeTag type={t.type} />
                        <span className="flex-1 min-w-0">
                            <span className="font-mono text-slate-400">{t.tipCode}</span>{' '}
                            {t.title}
                        </span>
                    </label>
                ))}
                <div className="text-[11px] text-slate-400">
                    贴士条目是全局库, 新建后即刻入库 (非管理员会转审批); 套件只引用它的编码。
                </div>
            </div>

            <div className="card p-4 space-y-2">
                <div className="text-sm font-semibold">已选 ({picked.length})</div>
                {picked.length === 0 ? (
                    <div className="text-xs text-slate-400">还没有选任何小贴士。</div>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {picked.map((code) => (
                            <span
                                key={code}
                                className="tag bg-slate-100 text-slate-600 cursor-pointer"
                                title="点击移除"
                                onClick={() => toggle(code)}
                            >
                                {byCode[code]?.title || code} ✕
                            </span>
                        ))}
                    </div>
                )}
                <FieldIssue issue={issueByPath['tips']} />
            </div>

            {creating && (
                <Modal open title="新建小贴士条目" width={640} onClose={() => setCreating(false)}>
                    <TipForm
                        onCancel={() => setCreating(false)}
                        onSaved={() => {
                            setCreating(false);
                            // 条目已入库, 重拉全量让它可被搜到 (转审批的条目还搜不到, 符合预期)
                            listAllTips()
                                .then(setAll)
                                .catch(() => undefined);
                        }}
                    />
                </Modal>
            )}
        </>
    );
}

export default StepRelations;
